import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import "./CategoryImageManager.css";

export default function CategoryImageManager() {
  const { auth } = useAuth();
  const [projectType, setProjectType] = useState("");
  const [existingTypes, setExistingTypes] = useState([]);
  const [images, setImages] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetch("/api/admin/intake-templates", {
      headers: { Authorization: `Bearer ${auth.token}` },
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => setExistingTypes(data.map((t) => t.projectType)))
      .catch(() => {});
  }, [auth.token]);

  const loadImages = async (type) => {
    if (!type) {
      setImages([]);
      return;
    }
    try {
      const res = await fetch(`/api/admin/category-images/${type}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
        cache: "no-store",
      });
      if (res.ok) {
        setImages(await res.json());
      } else {
        setImages([]);
      }
    } catch {
      setImages([]);
    }
  };

  const handleUpload = async () => {
    if (!file || !projectType) return;
    setStatus(null);
    setUploading(true);
    try {
      // Step 1: get a presigned URL from our server
      const presignRes = await fetch("/api/admin/category-images/presign", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({ fileName: file.name, contentType: file.type }),
      });
      const presignData = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignData.error || "Failed to get upload URL");

      // Step 2: upload the file DIRECTLY to S3 using that URL
      const uploadRes = await fetch(presignData.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("Upload to storage failed");

      // Step 3: save the CategoryImage record pointing at the uploaded file
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      const saveRes = await fetch("/api/admin/category-images", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          projectType,
          mediaType,
          role: "cover",
          url: presignData.publicUrl,
          altText: `${projectType.replace(/_/g, " ")} example`,
        }),
      });
      const saveData = await saveRes.json();
      if (!saveRes.ok) throw new Error(saveData.error || "Failed to save record");

      setStatus({ type: "success", message: "Uploaded and saved." });
      setFile(null);
      loadImages(projectType);
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`/api/admin/category-images/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      loadImages(projectType);
    } catch {
      setStatus({ type: "error", message: "Failed to delete." });
    }
  };

  return (
    <div className="cim-wrap">
      <h2>Category Images &amp; Video</h2>

      <div className="cim-field">
        <label>Project type</label>
        <input
          list="existing-cim-types"
          value={projectType}
          onChange={(e) => setProjectType(e.target.value)}
          onBlur={(e) => loadImages(e.target.value.trim())}
          placeholder="e.g. redecoration_interior"
        />
        <datalist id="existing-cim-types">
          {existingTypes.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </div>

      {projectType && (
        <>
          <div className="cim-field">
            <label>Upload a default image or video</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>

          <button
            className="cim-upload-btn"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>

          {status && (
            <div className={`cim-status ${status.type}`}>{status.message}</div>
          )}

          <div className="cim-gallery">
            {images.map((img) => (
              <div className="cim-card" key={img._id}>
                {img.mediaType === "video" ? (
                  <video src={img.url} controls muted />
                ) : (
                  <img src={img.url} alt={img.altText} />
                )}
                <div className="cim-card-info">
                  <span className="cim-type-badge">{img.mediaType}</span>
                  <br />
                  <button className="cim-delete-btn" onClick={() => handleDelete(img._id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
